import React, { useState, useMemo } from 'react';
import { FileSignature, AlertTriangle, ArrowUpDown, X, EyeOff, ShieldCheck, Brain } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { localize } from '../../data/i18nData';
import { estimateContractMargin } from '../../utils/contractMargin';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../store/stableEmpties';
import { getLoyaltyTier } from '../../data/clientLoyalty';
import { resolveClient } from '../../utils/resolveClient';
import canExecuteNow, { explainContractBlock } from '../../utils/canExecuteNow';
import { frigoZoneBlocked } from '../../utils/contractCompatibility';
import { getContractValidityDaysLeft } from '../../store/helpers';
import { evaluateContractCoverage } from '../../data/depotGeo';
import { findCityById } from '../../data/mapCities';
import ClientLogo from '../crm/ClientLogo';

const SORT_OPTION_KEYS = [
  { id: 'default',    labelKey: 'contracts_sort_default' },
  { id: 'margin',     labelKey: 'contracts_sort_margin' },
  { id: 'revenue',    labelKey: 'contracts_sort_revenue' },
  { id: 'category',   labelKey: 'contracts_sort_category' },
  { id: 'duration',   labelKey: 'contracts_sort_duration' },
  { id: 'tier',       labelKey: 'contracts_sort_tier' },
];

// 13/06/2026 — Persistance des préférences de tri/filtre (retour
// utilisateur : « c'est dommage que le tri choisi saute régulièrement »).
const SORT_STORAGE_KEY = 'hst-contract-sort';
const FREQ_STORAGE_KEY = 'hst-contract-freq';
// 17/06/2026 — Filtre « masquer les contrats non rentables » (cf. mail
// du commercial support `cli-hide-unprofitable-01`).
const HIDE_UNPROFITABLE_KEY = 'hst-contract-hide-unprofitable';

const readPref = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};
const writePref = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* quota/private mode */ }
};

const FREQ_FILTERS = [
  { id: 'all',      labelKey: 'contracts_filter_all' },
  { id: 'ponctuel', labelKey: 'contracts_filter_ponctuel' },
  { id: 'régulier', labelKey: 'contracts_filter_regulier' },
];

/**
 * 03/06/2026 — Refonte en vrai <table> compact (sans scroll), sur retour
 * utilisateur : « Je veux que les contrats soit affichés dans un tableau
 * complet dans lequel nous n'avons pas besoin de scroller. »
 * Colonnes : Nom | Client | Catégorie | Type | Tier | Prix | Durée | Marge | Action.
 *
 * 04/06/2026 — Responsable d'exploitation feature: when the support role
 * `role-christophe-faure` is hired, every row gets a "🟢 Prêt" badge if a
 * compatible idle vehicle + driver are available right now. The player can
 * also tick a checkbox to filter only ready contracts and the default sort
 * automatically promotes them to the top.
 */
export function AvailableContracts({
  availableContracts, drivers, vehicles, freeDrivers,
  marginUnlocked, canAcceptContract, onAccept,
  hasMarketIntelAccess, clientArgumentationDefaults, onResetArgumentation,
}) {
  const { t } = useTranslation();
  const canSort = !!hasMarketIntelAccess;
  const [sortBy, setSortByRaw] = useState(() => readPref(SORT_STORAGE_KEY, 'default'));
  const [freqFilter, setFreqFilterRaw] = useState(() => readPref(FREQ_STORAGE_KEY, 'all'));
  const [onlyReady, setOnlyReady] = useState(false);
  // 17/06/2026 — Toggle off par défaut ; activable via un mail du
  // commercial support (cf. data/messageTemplates.js).
  const [hideUnprofitable, setHideUnprofitableRaw] = useState(
    () => readPref(HIDE_UNPROFITABLE_KEY, '0') === '1',
  );

  const setSortBy = (v) => { setSortByRaw(v); writePref(SORT_STORAGE_KEY, v); };
  const setFreqFilter = (v) => { setFreqFilterRaw(v); writePref(FREQ_STORAGE_KEY, v); };
  const setHideUnprofitable = (v) => {
    setHideUnprofitableRaw(v);
    writePref(HIDE_UNPROFITABLE_KEY, v ? '1' : '0');
  };

  const hiddenContractIds = useGameStore(s => s.hiddenContractIds || EMPTY_ARRAY);
  const hideAvailableContract = useGameStore(s => s.hideAvailableContract);
  const restoreHiddenContracts = useGameStore(s => s.restoreHiddenContracts);
  const totalDays = useGameStore(s => s.totalDays || 0);
  // 13/06/2026 — contrats frigo T3+ : badge « Zone frigo requise » si
  // l'investissement inv-depot-05 n'est pas actif.
  const activeInvestments = useGameStore(s => s.activeInvestments || EMPTY_OBJECT);
  // 13/06/2026 — Chantier Dépôts : couverture géographique des contrats.
  const ownedDepots = useGameStore(s => s.ownedDepots || EMPTY_ARRAY);
  const company = useGameStore(s => s.company);
  // 04/06/2026 — `Responsable d'exploitation` unlocks the readiness UI.
  const hasOps = useGameStore(s => !!(s.hiredSupport || {})['role-christophe-faure']);

  // Compute readiness for every visible contract ONCE per render.
  const visibleContracts = useMemo(() => (
    hiddenContractIds.length === 0
      ? availableContracts
      : availableContracts.filter(c => !hiddenContractIds.includes(c.instanceId))
  ), [availableContracts, hiddenContractIds]);

  const readinessById = useMemo(() => {
    if (!hasOps) return {};
    const map = {};
    visibleContracts.forEach(c => {
      // 15/06/2026 — readiness = vraie paire véhicule+chauffeur compatible
      // (mêmes règles que la modale d'assignation) + zone frigo dépôt.
      map[c.instanceId] = canExecuteNow(c, vehicles, drivers, { activeInvestments, ownedDepots });
    });
    return map;
  }, [hasOps, visibleContracts, vehicles, drivers, activeInvestments, ownedDepots]);

  const filteredContracts = useMemo(() => {
    let list = visibleContracts;
    if (freqFilter !== 'all') {
      list = list.filter(c => (c.frequency || '') === freqFilter);
    }
    if (hasOps && onlyReady) {
      list = list.filter(c => readinessById[c.instanceId]?.ready);
    }
    // 17/06/2026 — Masquer les contrats non rentables (marge nette ≤ 0).
    // Le filtre n'a de sens que si la rentabilité est débloquée (sinon on
    // n'a pas l'info). Disponible aussi si la marge ne peut être calculée
    // → on garde le contrat (pas de faux négatif sur les compatibilités).
    //
    // 09/02/2026 — Q2 Lot I user prod : retour user « le bouton ne fait
    // pas disparaître les marges négatives ». Cause : le filtre regardait
    // la marge BRUTE (avant pénalité de couverture zone) alors que la
    // colonne affichée applique la pénalité. Les contrats hors-zone
    // étaient donc montrés en rouge mais NON masqués. On aligne maintenant
    // le filtre sur la marge DISPLAY (incluant la pénalité geo).
    if (marginUnlocked && hideUnprofitable) {
      list = list.filter(c => {
        const mRaw = estimateContractMargin(c, vehicles, drivers);
        if (!mRaw) return true; // pas d'estimation → on garde par sécurité
        const cov = evaluateContractCoverage(c, ownedDepots, company, activeInvestments);
        // Marge affichée = brute - pénalité couverture
        const displayedMargin = cov.covered
          ? mRaw.margin
          : Math.round(mRaw.margin - mRaw.revenue * (cov.penalty || 0));
        return displayedMargin > 0;
      });
    }
    return list;
  }, [hasOps, onlyReady, visibleContracts, readinessById, freqFilter, marginUnlocked, hideUnprofitable, vehicles, drivers, ownedDepots, company, activeInvestments]);

  const hiddenCount = availableContracts.length - visibleContracts.length;

  // 14/06/2026 — Indication explicite : N contrats frigo nécessitent
  // l'investissement « Zone frigorifique » au dépôt (bandeau sous le tableau).
  const frigoBlockedCount = useMemo(
    () => visibleContracts.filter(c => frigoZoneBlocked(c, activeInvestments, ownedDepots)).length,
    [visibleContracts, activeInvestments, ownedDepots],
  );

  const sorted = useMemo(() => {
    // 04/06/2026 — Default sort = newest-first by spawnDay. The user
    // reported that incoming contracts visually mix with older ones, making
    // it hard to spot what's new. We now anchor the new arrivals at the
    // top (descending spawnDay) and tie-break alphabetically for stability.
    // When the Responsable d'exploitation is hired, "ready" contracts are
    // further promoted to the very top (the player can execute immediately).
    const byNewest = (a, b) => {
      // Ready-first promotion (only when ops is hired)
      if (hasOps) {
        const ra = readinessById[a.instanceId]?.ready ? 1 : 0;
        const rb = readinessById[b.instanceId]?.ready ? 1 : 0;
        if (ra !== rb) return rb - ra;
      }
      const dA = a.spawnDay ?? 0;
      const dB = b.spawnDay ?? 0;
      if (dA !== dB) return dB - dA;
      return (a.name || '').localeCompare(b.name || '');
    };
    if (!canSort || sortBy === 'default') {
      return [...filteredContracts].sort(byNewest);
    }
    const withScore = filteredContracts.map(c => {
      const m = estimateContractMargin(c, vehicles, drivers);
      const grossRevenue = c.frequency === 'ponctuel' ? (c.revenue || 0) : (c.dailyRevenue || 0) * (c.duration || 1);
      return { c, margin: m ? m.margin : -Infinity, marginPct: m ? m.marginPct : -Infinity, grossRevenue };
    });
    if (sortBy === 'margin')   withScore.sort((a, b) => b.margin - a.margin);
    if (sortBy === 'revenue')  withScore.sort((a, b) => b.grossRevenue - a.grossRevenue);
    if (sortBy === 'category') withScore.sort((a, b) => (a.c.category || '').localeCompare(b.c.category || ''));
    if (sortBy === 'duration') withScore.sort((a, b) => (b.c.duration || 0) - (a.c.duration || 0));
    if (sortBy === 'tier')     withScore.sort((a, b) => (a.c.tierMin || 1) - (b.c.tierMin || 1) || b.grossRevenue - a.grossRevenue);
    return withScore.map(x => x.c);
  }, [filteredContracts, sortBy, canSort, vehicles, drivers, hasOps, readinessById]);

  return (
    <div className="bg-ink-800 rounded-md border border-ink-600 overflow-hidden" data-testid="available-contracts-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ink-600">
        <div className="flex items-center gap-2">
          <FileSignature className="w-3.5 h-3.5 text-accent" />
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            {t('available_contracts_title')}
          </h3>
          <span className="font-mono text-[10px] text-slate-500 tabular">[{visibleContracts.length}]</span>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={restoreHiddenContracts}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-ink-700/50 hover:bg-ink-700 text-slate-400 hover:text-slate-200 font-mono border border-ink-600 transition-colors"
              data-testid="restore-hidden-contracts-btn"
              title={t('contracts_restore_hidden_tooltip')}
            >
              <EyeOff className="w-3 h-3" />
              {t('contracts_restore_hidden_label', { count: hiddenCount })}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* 13/06/2026 — Switch Ponctuel / Régulier (toujours visible) */}
          <div
            className="flex items-center rounded border border-ink-600 overflow-hidden"
            data-testid="contract-freq-filter"
          >
            {FREQ_FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFreqFilter(f.id)}
                className={`text-[9px] px-2 py-1 font-mono uppercase tracking-wider transition-colors ${
                  freqFilter === f.id
                    ? 'bg-accent text-white'
                    : 'bg-ink-700/40 text-slate-400 hover:text-slate-200'
                }`}
                data-testid={`contract-freq-${f.id === 'régulier' ? 'regulier' : f.id}`}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
          {hasOps && (
            <label
              className="inline-flex items-center gap-1.5 text-[10px] text-emerald-300 cursor-pointer select-none"
              data-testid="ops-ready-filter-label"
              title={t('contracts_ready_filter_tooltip')}
            >
              <input
                type="checkbox"
                checked={onlyReady}
                onChange={(e) => setOnlyReady(e.target.checked)}
                className="w-3 h-3 accent-emerald-500"
                data-testid="ops-ready-filter-checkbox"
              />
              <ShieldCheck className="w-3 h-3" />
              <span className="font-mono uppercase tracking-wider">{t('contracts_ready_filter_label')}</span>
            </label>
          )}
          {/* 17/06/2026 — Filtre "masquer non rentables", visible une fois la
              rentabilité débloquée (advisor finance). Activé via mail du
              commercial support. */}
          {marginUnlocked && (
            <label
              className="inline-flex items-center gap-1.5 text-[10px] text-amber-300 cursor-pointer select-none"
              data-testid="hide-unprofitable-label"
              title="Masque les contrats dont la marge nette estimée est ≤ 0 €"
            >
              <input
                type="checkbox"
                checked={hideUnprofitable}
                onChange={(e) => setHideUnprofitable(e.target.checked)}
                className="w-3 h-3 accent-amber-500"
                data-testid="hide-unprofitable-checkbox"
              />
              <EyeOff className="w-3 h-3" />
              <span className="font-mono uppercase tracking-wider">Masquer non rentables</span>
            </label>
          )}
          {canSort && (
            <div className="flex items-center gap-1.5" data-testid="contract-sort-control">
              <ArrowUpDown className="w-3 h-3 text-slate-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-[10px] bg-ink-700 text-slate-200 border border-ink-600 rounded px-1.5 py-0.5 font-mono focus:outline-none focus:border-accent"
                data-testid="contract-sort-select"
                title={t('contracts_sort_tooltip')}
              >
                {SORT_OPTION_KEYS.map(opt => (
                  <option key={opt.id} value={opt.id}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="p-3">
        {sorted.length === 0 ? (
          <p className="text-xs text-slate-500 px-1">{t('no_contracts')}</p>
        ) : (
          <table className="w-full text-[11px] border-collapse" data-testid="contracts-table">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-slate-500 font-mono border-b border-ink-600">
                <th className="text-left px-2 py-1.5 font-normal">{t('contracts_col_name')}</th>
                <th className="text-left px-2 py-1.5 font-normal">{t('contracts_col_client')}</th>
                <th className="text-left px-2 py-1.5 font-normal">{t('contracts_col_category')}</th>
                <th className="text-left px-2 py-1.5 font-normal">{t('contracts_col_freq')}</th>
                <th className="text-center px-2 py-1.5 font-normal">{t('contracts_col_tier')}</th>
                <th className="text-right px-2 py-1.5 font-normal">{t('contracts_col_price')}</th>
                <th className="text-right px-2 py-1.5 font-normal">{t('contracts_col_duration')}</th>
                {marginUnlocked && (
                  <th className="text-right px-2 py-1.5 font-normal">{t('contracts_col_margin')}</th>
                )}
                <th className="text-right px-2 py-1.5 font-normal">{t('contracts_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(rawC => {
                const c = localize('contract', rawC);
                // 13/06/2026 — couverture géo : marge affichée nette de la
                // pénalité hors zone (≥ 40 %), badges ville/distance.
                const coverage = evaluateContractCoverage(c, ownedDepots, company, activeInvestments);
                const originCity = c.originCityId ? findCityById(c.originCityId) : null;
                const mRaw = marginUnlocked ? estimateContractMargin(c, vehicles, drivers) : null;
                const m = mRaw && !coverage.covered
                  ? {
                      ...mRaw,
                      margin: Math.round(mRaw.margin - mRaw.revenue * coverage.penalty),
                      revenue: Math.round(mRaw.revenue * (1 - coverage.penalty)),
                      marginPct: mRaw.revenue > 0
                        ? Math.round(((mRaw.margin - mRaw.revenue * coverage.penalty) / Math.max(1, mRaw.revenue * (1 - coverage.penalty))) * 100)
                        : mRaw.marginPct,
                    }
                  : mRaw;
                const neg = m && m.margin < 0;
                // 04/06/2026 — "NEW" badge for contracts spawned ≤ 2 days
                // ago so the player spots fresh arrivals among the list.
                const isFresh = Number.isFinite(c.spawnDay) && (totalDays - c.spawnDay) <= 2;
                // 16/06/2026 — validité de l'offre (TTL 14 j) affichée au joueur.
                const validityDays = getContractValidityDaysLeft(c, totalDays);
                const readiness = hasOps ? readinessById[c.instanceId] : null;
                return (
                  <tr
                    key={c.instanceId}
                    className={`border-b border-ink-700/50 last:border-b-0 transition-colors ${
                      c.isSpecial ? 'bg-warn/5 hover:bg-warn/10'
                      : readiness?.ready ? 'bg-emerald-500/10 hover:bg-emerald-500/20'
                      : isFresh ? 'bg-emerald-500/5 hover:bg-emerald-500/10'
                      : 'hover:bg-ink-700/40'
                    }`}
                    data-testid={`contract-row-${c.instanceId}`}
                  >
                    {/* Nom */}
                    <td className="px-2 py-2 max-w-[180px]">
                      <div className="flex items-center gap-1 flex-wrap">
                        {readiness?.ready && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-emerald-600 text-white font-mono font-black uppercase tracking-wider flex-shrink-0 inline-flex items-center gap-0.5"
                            data-testid={`ready-badge-${c.instanceId}`}
                            title={t('contracts_ready_tooltip')}
                          >
                            🟢 {t('contracts_ready_badge')}
                          </span>
                        )}
                        {isFresh && !c.isSpecial && !readiness?.ready && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-emerald-500 text-ink-900 font-mono font-black uppercase tracking-wider flex-shrink-0"
                            data-testid={`new-badge-${c.instanceId}`}
                            title={t('contracts_new_tooltip')}
                          >
                            {t('contracts_new_badge')}
                          </span>
                        )}
                        {c.isSpecial && (
                          <span className="text-[8px] px-1 py-0.5 rounded-sm bg-warn text-ink-900 font-mono font-black uppercase tracking-wider flex-shrink-0">
                            {t('contracts_special_badge')}
                          </span>
                        )}
                        {frigoZoneBlocked(c, activeInvestments, ownedDepots) && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-cyan-600/20 text-cyan-300 border border-cyan-500/40 font-mono font-bold uppercase tracking-wider flex-shrink-0"
                            data-testid={`frigo-zone-badge-${c.instanceId}`}
                            title={t('contracts_frigo_zone_tooltip')}
                          >
                            ❄ {t('contracts_frigo_zone_badge')}
                          </span>
                        )}
                        {!coverage.covered && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-amber-600/20 text-amber-300 border border-amber-500/40 font-mono font-bold uppercase tracking-wider flex-shrink-0"
                            data-testid={`out-of-zone-badge-${c.instanceId}`}
                            title={t('contracts_out_of_zone_tooltip', {
                              city: originCity?.name || '?',
                              distance: coverage.distanceKm,
                              pct: Math.round(coverage.penalty * 100),
                            })}
                          >
                            {t('contracts_out_of_zone_badge', { pct: Math.round(coverage.penalty * 100) })}
                          </span>
                        )}
                        {originCity && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-ink-700/60 text-slate-400 font-mono flex-shrink-0"
                            data-testid={`origin-city-${c.instanceId}`}
                            title={coverage.covered
                              ? t('contracts_origin_covered_tooltip', { city: originCity.name, distance: coverage.distanceKm })
                              : t('contracts_out_of_zone_tooltip', { city: originCity.name, distance: coverage.distanceKm, pct: Math.round(coverage.penalty * 100) })}
                          >
                            📍 {originCity.name}{coverage.distanceKm > 0 ? ` · ${coverage.distanceKm} km` : ''}
                          </span>
                        )}
                        {c.rareOpportunity && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-purple-600/20 text-purple-300 border border-purple-500/40 font-mono font-bold uppercase tracking-wider flex-shrink-0"
                            data-testid={`rare-badge-${c.instanceId}`}
                            title={t('contracts_rare_tooltip')}
                          >
                            💎 {t('contracts_rare_badge')}
                          </span>
                        )}
                        {validityDays != null && (
                          <span
                            className={`text-[8px] px-1 py-0.5 rounded-sm font-mono font-bold uppercase tracking-wider flex-shrink-0 border ${
                              validityDays <= 2 ? 'bg-danger/20 text-danger border-danger/40'
                              : validityDays <= 5 ? 'bg-amber-600/20 text-amber-300 border-amber-500/40'
                              : 'bg-ink-700/60 text-slate-400 border-ink-600'
                            }`}
                            data-testid={`validity-badge-${c.instanceId}`}
                            title={t('contracts_validity_tooltip', { days: validityDays })}
                          >
                            ⏳ {t('contracts_validity_badge', { days: validityDays })}
                          </span>
                        )}
                        <p className="text-[11px] font-semibold text-slate-100 truncate" title={c.name}>{c.name}</p>
                      </div>
                      {c.isSpecial && c.requiresLicense && (
                        <span className="inline-block mt-0.5 text-[9px] px-1 py-0 rounded-sm bg-danger/15 text-danger border border-danger/30 font-mono">
                          {t('contracts_permit_label')} {c.requiresLicense}
                        </span>
                      )}
                      {/* 10/02/2026 (Lot N P1) — Retour user prod : « Que
                          faut-il pour répondre à ce type de contrat ? Cela
                          signifie quoi spécialisé ? ». Affiche désormais la
                          spécialité requise + un tooltip pédagogique. */}
                      {c.requiresSpecialty && (
                        <span
                          className="inline-block mt-0.5 ml-1 text-[9px] px-1 py-0 rounded-sm bg-purple-500/15 text-purple-300 border border-purple-500/40 font-mono"
                          title={`Équipement véhicule requis : ${c.requiresSpecialty}. Le camion assigné doit porter cette spécialité.`}
                          data-testid={`contract-specialty-badge-${c.instanceId}`}
                        >
                          🎯 {c.requiresSpecialty}
                        </span>
                      )}
                    </td>
                    {/* Client */}
                    <td className="px-2 py-2 max-w-[160px]">
                      <div className="flex items-center gap-2">
                        <ClientLogo
                          client={resolveClient(c)}
                          clientId={c.clientId}
                          category={c.category}
                          size="sm"
                          className="flex-shrink-0"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-[10px] text-slate-300 font-medium truncate" title={c.clientName}>
                            {c.clientName || '—'}
                          </span>
                          {c.clientId && <ClientLoyaltyTag clientId={c.clientId} />}
                        </div>
                      </div>
                    </td>
                    {/* Catégorie */}
                    <td className="px-2 py-2">
                      <span className="text-[10px] text-slate-500 whitespace-nowrap">{c.category}</span>
                    </td>
                    {/* Fréquence */}
                    <td className="px-2 py-2">
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-accent/10 text-accent border border-accent/30 font-mono uppercase tracking-wider whitespace-nowrap">
                        {c.frequency === 'ponctuel' ? t('freq_ponctuel') : c.frequency === 'régulier' ? t('freq_regulier') : c.frequency}
                      </span>
                    </td>
                    {/* Tier */}
                    <td className="px-2 py-2 text-center">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-ink-700 text-slate-400 font-mono uppercase tracking-wider">
                        T{c.tierMin}+
                      </span>
                    </td>
                    {/* Prix */}
                    <td className="px-2 py-2 text-right">
                      <span className="data-stat text-[12px] font-bold text-money font-mono whitespace-nowrap">
                        {c.frequency === 'ponctuel' ? formatMoney(c.revenue) : `${formatMoney(c.dailyRevenue)}/j`}
                      </span>
                    </td>
                    {/* Durée */}
                    <td className="px-2 py-2 text-right">
                      <span className="text-[10px] text-slate-400 font-mono">
                        {c.duration && c.duration > 1 ? `${c.duration}j` : '—'}
                      </span>
                    </td>
                    {/* Marge */}
                    {marginUnlocked && (
                      <td className="px-2 py-2 text-right">
                        {m ? (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold ${neg ? 'text-danger' : 'text-money'}`}
                            data-testid={`margin-cell-${c.instanceId}`}
                            title={t('contracts_margin_tooltip', { revenue: formatMoney(m.revenue), charges: formatMoney(m.charges), margin: formatMoney(m.margin), pct: m.marginPct })}
                          >
                            {formatMoney(m.margin)}
                            <span className="opacity-70">({m.marginPct}%)</span>
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-600">—</span>
                        )}
                      </td>
                    )}
                    {/* Actions */}
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); hideAvailableContract(c.instanceId); }}
                        className="inline-flex w-5 h-5 items-center justify-center rounded text-slate-500 hover:text-danger hover:bg-danger/10 transition-colors mr-1 align-middle"
                        data-testid={`hide-contract-${c.instanceId}`}
                        title={t('contracts_hide_tooltip')}
                        aria-label={t('contracts_hide_aria')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {/* 11/06/2026 — Memorized argumentation indicator + reset. Only
                          shown when a default exists for this contract's client. */}
                      {(() => {
                        const memo = clientArgumentationDefaults && c.clientId
                          ? clientArgumentationDefaults[c.clientId] : null;
                        if (!memo || !onResetArgumentation) return null;
                        return (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onResetArgumentation(c); }}
                            className="inline-flex w-5 h-5 items-center justify-center rounded text-purple-300 hover:text-purple-100 hover:bg-purple-500/15 transition-colors mr-1 align-middle"
                            data-testid={`reset-argumentation-${c.instanceId}`}
                            title={`Argumentation mémorisée : « ${memo.choice} ». Cliquez pour réinitialiser et choisir à nouveau.`}
                            aria-label="Changer l'argumentation"
                          >
                            <Brain className="w-3 h-3" />
                          </button>
                        );
                      })()}
                      {(() => {
                        const blocked = !canAcceptContract(c);
                        // 20/07/2026 — tooltip précis quand ACCEPTER est grisé
                        // (span wrapper : les boutons disabled n'affichent pas
                        // toujours leur title selon le navigateur).
                        const blockReason = blocked
                          ? explainContractBlock(c, vehicles, drivers, { activeInvestments, ownedDepots })
                          : null;
                        return (
                          <span title={blockReason || undefined} data-testid={blocked ? `accept-blocked-reason-${c.instanceId}` : undefined}>
                            <button
                              onClick={() => onAccept(c)}
                              disabled={blocked}
                              className={`text-[10px] px-2.5 py-1 rounded-md font-mono font-bold uppercase tracking-wider transition-all align-middle ${
                                blocked
                                  ? 'bg-ink-700 text-slate-600 cursor-not-allowed border border-ink-600'
                                  : c.isSpecial
                                    ? 'bg-warn text-ink-900 hover:shadow-glow-accent border border-warn'
                                    : 'bg-accent/15 text-accent border border-accent/40 hover:bg-accent hover:text-white'
                              }`}
                              data-testid={`accept-contract-${c.instanceId}`}
                            >
                              {t('accept')}
                            </button>
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {frigoBlockedCount > 0 && (
          <div className="bg-cyan-500/5 border border-cyan-500/30 rounded-md px-3 py-2 mt-2" data-testid="frigo-zone-hint">
            <p className="text-[11px] text-cyan-300 font-medium flex items-start gap-1.5">
              <span className="flex-shrink-0">❄</span>
              <span>{t('contracts_frigo_zone_hint', { count: frigoBlockedCount })}</span>
            </p>
          </div>
        )}

        {freeDrivers.length === 0 && drivers.length === 0 && (
          <div className="bg-warn/5 border border-warn/30 rounded-md px-3 py-2 mt-2" data-testid="no-drivers-hint">
            <p className="text-[11px] text-warn font-medium flex items-center gap-1 font-mono uppercase tracking-wider">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {t('to_accept_contracts')}
            </p>
            <ol className="text-[11px] text-slate-300 mt-1 ml-5 list-decimal space-y-0.5">
              <li>{t('go_to_hr_step')}</li>
              <li>{t('assign_driver_step')}</li>
              <li>{t('come_back_step')}</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact loyalty badge — shows the tier label as a 9px chip with the
 * tier color as background. Used below the client name in the contracts
 * table to give an at-a-glance relationship signal.
 */
function ClientLoyaltyTag({ clientId }) {
  const score = useGameStore(s => s.clientLoyalty?.[clientId]?.score);
  if (!clientId || score == null) return null;
  const tier = getLoyaltyTier(score);
  if (!tier) return null;
  return (
    <span
      data-testid={`contract-loyalty-tag-${clientId}`}
      className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider mt-0.5"
      style={{ color: tier.color }}
      title={`${tier.label} (${Math.round(score)}/100)`}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: tier.color, boxShadow: `0 0 0 1px ${tier.color}40` }}
      />
      {tier.label}
    </span>
  );
}
